import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, CardFooter, Container } from "reactstrap";
import Loader from "../../Loader";
import { utils } from "../../../utils/utils";
import AdvanceTable from "../../advanceTable/AdvanceTable";
import { programmingApi } from "../../../services/programmingServices";
import AdvanceTableWrapper from "../../advanceTable/AdvanceTableWrapper";
import AdvanceTablePagination from "../../advanceTable/AdvanceTablePagination";
import { useAuth } from "../../../providers/authProvider";

const PROGRAMMER_ROLE_ID = 6; // Programmer role ID
const TABLE_OPTIONS = {
  sizePerPage: 25,
  page: 1,
  sortBy: "createdAt",
  direction: "asc",
  totalPages: 1,
};

const DEFAULT_COLUMNS = [
  {
    id: "jobNumber",
    accessor: "job",
    Header: "Job#",
    headerProps: { className: "text-truncate" },
    width: 100,
    Cell: (rowData) => {
      const { job } = rowData.row.original;
      return job?.jobNumber || "-";
    },
  },
  {
    id: "customerName",
    accessor: "job",
    Header: "Customer Name",
    headerProps: { className: "text-truncate" },
    width: 150,
    Cell: (rowData) => {
      const { job } = rowData.row.original;
      return job?.customer?.name || "-";
    },
  },
  {
    id: "description",
    accessor: "description",
    disableSortBy: true,
    Header: "Description",
    headerProps: { className: "text-truncate" },
    width: 150,
  },
  {
    id: "revisionDate",
    accessor: "revisionDate",
    Header: "Rev Date",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      const { revisionDate } = rowData.row.original;
      return revisionDate ? utils.formatDate(revisionDate) : "-";
    },
  },
  {
    id: "notes",
    accessor: "notes",
    Header: "PM Notes",
    headerProps: { className: "text-truncate" },
    disableSortBy: true,
    width: 250,
    Cell: (rowData) => {
      const { notes } = rowData.row.original;
      return notes || "-";
    },
  },
];

const PROGRAMMER_COLUMNS = [
  {
    accessor: "updatedAt",
    Header: "Assigned At",
    headerProps: { className: "text-truncate" },
    disableSortBy: true,
    width: 120,
    Cell: (rowData) => {
      const { updatedAt } = rowData.row.original;
      let dateString = "-";
      if (updatedAt) {
        dateString = utils.formatDate(updatedAt, "LT MM/DD/YY");
      }
      return dateString;
    },
  },
  {
    id: "programmerName",
    accessor: "programmer",
    Header: "Programmer",
    headerProps: { className: "text-truncate" },
    width: 125,
    Cell: (rowData) => {
      const programming = rowData.row.original;
      const programmer = programming?.programmer;
      return programmer
        ? `${programmer?.firstName} ${programmer?.lastName}`
        : "-";
    },
  },
  {
    id: "checkerName",
    accessor: "checker",
    Header: "Checker",
    headerProps: { className: "text-truncate" },
    width: 125,
    Cell: (rowData) => {
      const programming = rowData.row.original;
      const checker = programming?.checker;
      return checker ? `${checker?.firstName} ${checker?.lastName}` : "-";
    },
  },
];

const ProgrammingTable = ({
  columns = [],
  programmingStatusId,
  programmingTotal,
  setProgrammingTotal,
  reloadTabs = [],
  tabName,
}) => {
  const [authContext] = useAuth();
  const [loading, setLoading] = useState();
  const [programming, setProgramming] = useState([]);
  const [tableOptions, setTableOptions] = useState(TABLE_OPTIONS);
  const isProgrammer = authContext.currentUser?.role?.id === PROGRAMMER_ROLE_ID;

  // Load programming data
  useEffect(() => {
    const loadProgramming = async () => {
      try {
        if (reloadTabs.includes(tabName)) {
          setLoading(true);
          const programming = await programmingApi.getAllProgramming({
            page: tableOptions.page - 1,
            pageSize: tableOptions.sizePerPage,
            sortBy: tableOptions.sortBy,
            direction: tableOptions.direction,
            statusId: programmingStatusId,
          });
          setLoading(false);
          setProgramming(programming.data);
          setProgrammingTotal(programming.count);
        }
      } catch (err) {
        setLoading(false);
      }
    };
    loadProgramming();
  }, [
    setProgrammingTotal,
    programmingStatusId,
    tableOptions.page,
    tableOptions.sizePerPage,
    tableOptions.sortBy,
    tableOptions.direction,
    reloadTabs,
    tabName,
  ]);

  const onSort = useCallback(
    ([data]) => {
      if (data) {
        const sortBy = data.id;
        const direction = data.desc ? "desc" : "asc";
        if (
          tableOptions.sortBy === sortBy?.id &&
          tableOptions.direction === direction
        ) {
          return;
        }

        setTableOptions({
          ...tableOptions,
          sortBy,
          direction,
        });
      } else {
        setTableOptions({
          ...tableOptions,
          sortBy: null,
          direction: null,
        });
      }
    },
    [tableOptions]
  );

  return (
    <Container fluid className="px-0">
      <div className="w-100">
        {loading ? (
          <Loader size="sm" />
        ) : (
          <AdvanceTableWrapper
            columns={
              isProgrammer
                ? [...DEFAULT_COLUMNS, ...PROGRAMMER_COLUMNS, ...columns]
                : [...DEFAULT_COLUMNS, ...columns]
            }
            data={programming || []}
            pageSize={tableOptions.sizePerPage}
            sortable
            onSort={onSort}
            defaultSort={{
              sortBy: tableOptions.sortBy,
              direction: tableOptions.direction,
            }}
          >
            <Card className="mb-2">
              <CardBody className="col-12 px-0 overflow-x-auto">
                <AdvanceTable
                  table
                  isLoading={loading}
                  headerClassName="text-muted small"
                  tableProps={{
                    striped: true,
                    className: "mb-0",
                  }}
                />
              </CardBody>
              {!isProgrammer ? (
                <CardFooter>
                  <AdvanceTablePagination
                    rowsPerPageOptions={[5, 10, 15, 25, 50, 75, 100]}
                    totalCount={programmingTotal}
                    pageCount={tableOptions.totalPages}
                    currentPage={tableOptions.page - 1}
                    onPageChange={(page) =>
                      setTableOptions({
                        ...tableOptions,
                        page,
                      })
                    }
                    pageSize={tableOptions.sizePerPage}
                    onPageSizeChange={(sizePerPage) =>
                      setTableOptions({
                        ...tableOptions,
                        sizePerPage,
                      })
                    }
                  />
                </CardFooter>
              ) : null}
            </Card>
          </AdvanceTableWrapper>
        )}
      </div>
    </Container>
  );
};

export default ProgrammingTable;
